import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import toast from "react-hot-toast";
import {
  RiEyeLine,
  RiEyeOffLine,
} from "react-icons/ri";

export default function Login() {
  const { login, verifyOtp } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [otpCode, setOtpCode] = useState("");
  const [otpChallenge, setOtpChallenge] = useState(null);
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await login(form.email, form.password);
      if (result.otp_required) {
        setOtpChallenge(result);
        toast.success("Enter the code sent to your email");
      } else {
        toast.success("Welcome back!");
        navigate("/dashboard");
      }
    } catch (err) {
      toast.error(
        err.response?.data?.error ||
          err.response?.data?.errors?.[0]?.msg ||
          "Login failed",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await verifyOtp(otpChallenge.pending_token, otpCode);
      toast.success("Welcome back!");
      navigate("/dashboard");
    } catch (err) {
      toast.error(
        err.response?.data?.error ||
          err.response?.data?.errors?.[0]?.msg ||
          "OTP verification failed",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "var(--bg-primary)",
        padding: 20,
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Background glow */}
      <div
        style={{
          position: "absolute",
          top: "20%",
          left: "30%",
          width: 600,
          height: 600,
          background:
            "radial-gradient(circle, rgba(99,102,241,0.08) 0%, transparent 70%)",
          pointerEvents: "none",
        }}
      />

      <div style={{ width: "100%", maxWidth: 420 }} className="fade-in">
        {/* Logo */}
        <div style={{ textAlign: "center", marginBottom: 40 }}>
          <div
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
              marginBottom: 8,
            }}
          >
            <img
              src="/favicon.png"
              alt="FinTrack Logo"
              style={{ width: "60px", height: "60px", objectFit: "contain" }}
            />
          </div>
          <h1
            style={{
              fontSize: 32,
              fontWeight: 800,
              letterSpacing: "-0.03em",
              marginBottom: 6,
            }}
          >
            Welcome back
          </h1>
          <p style={{ color: "var(--text-secondary)", fontSize: 14 }}>
            Sign in to your FinTrack account
          </p>
        </div>

        <div className="card" style={{ padding: 32 }}>
          {otpChallenge ? (
            <form onSubmit={handleOtpSubmit}>
              <div className="form-group">
                <label className="form-label">Email OTP</label>
                <input
                  type="text"
                  inputMode="numeric"
                  className="form-input"
                  placeholder="6 digit code"
                  value={otpCode}
                  onChange={(e) =>
                    setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                  }
                  required
                />
              </div>
              <p
                style={{
                  fontSize: 12,
                  color: "var(--text-secondary)",
                  marginBottom: 16,
                }}
              >
                We sent a 6 digit code to {otpChallenge.email || form.email}. It
                expires in a few minutes.
              </p>
              <button
                type="submit"
                className="btn btn-primary btn-lg"
                style={{ width: "100%", justifyContent: "center" }}
                disabled={loading}
              >
                {loading ? (
                  <span
                    className="spinner"
                    style={{ width: 20, height: 20, borderWidth: 2 }}
                  />
                ) : (
                  "Verify Code"
                )}
              </button>
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                style={{
                  width: "100%",
                  justifyContent: "center",
                  marginTop: 12,
                }}
                onClick={() => setOtpChallenge(null)}
              >
                Back to password
              </button>
            </form>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="form-label">Email Address</label>
                <input
                  type="email"
                  className="form-input"
                  placeholder="you@example.com"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label className="form-label">Password</label>
                <div style={{ position: "relative" }}>
                  <input
                    type={showPass ? "text" : "password"}
                    className="form-input"
                    placeholder="••••••••"
                    value={form.password}
                    onChange={(e) =>
                      setForm({ ...form, password: e.target.value })
                    }
                    required
                    style={{ paddingRight: 44 }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPass(!showPass)}
                    style={{
                      position: "absolute",
                      right: 12,
                      top: "50%",
                      transform: "translateY(-50%)",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      color: "var(--text-muted)",
                      padding: 0,
                    }}
                  >
                    {showPass ? (
                      <RiEyeOffLine size={18} />
                    ) : (
                      <RiEyeLine size={18} />
                    )}
                  </button>
                </div>
                <div style={{ textAlign: "right", marginTop: 6 }}>
                  <Link
                    to="/forgot-password"
                    style={{
                      fontSize: 12,
                      color: "var(--accent-blue)",
                      textDecoration: "none",
                    }}
                  >
                    Forgot password?
                  </Link>
                </div>
              </div>

              <button
                type="submit"
                className="btn btn-primary btn-lg"
                style={{
                  width: "100%",
                  justifyContent: "center",
                  marginTop: 8,
                }}
                disabled={loading}
              >
                {loading ? (
                  <span
                    className="spinner"
                    style={{ width: 20, height: 20, borderWidth: 2 }}
                  />
                ) : (
                  "Sign In"
                )}
              </button>
            </form>
          )}

          {!otpChallenge && (
            <div
              style={{
                textAlign: "center",
                marginTop: 24,
                fontSize: 14,
                color: "var(--text-secondary)",
              }}
            >
              Don't have an account?{" "}
              <Link
                to="/register"
                style={{
                  color: "var(--accent-blue)",
                  fontWeight: 600,
                  textDecoration: "none",
                }}
              >
                Create one
              </Link>
            </div>
          )}
        </div>

        {/* Demo credentials */}
        <div
          style={{
            marginTop: 20,
            padding: "12px 16px",
            background: "rgba(99,102,241,0.08)",
            border: "1px solid rgba(99,102,241,0.2)",
            borderRadius: 10,
            fontSize: 12,
            color: "var(--text-secondary)",
            textAlign: "center",
          }}
        >
          💡 <strong style={{ color: "var(--text-primary)" }}>Demo:</strong>{" "}
          Register a new account to explore all features
        </div>
      </div>
    </div>
  );
}
